// tokenManager.ts
//
// Centralized bearer token management module.
//
// SECURITY CONTEXT:
// Previously the bearer token was stored in localStorage, which is accessible
// to ANY JavaScript running on the same origin. A single XSS vulnerability
// would allow an attacker to exfiltrate the token and impersonate the user.
//
// This module moves token storage to sessionStorage, which:
//   - Is scoped to the browser tab/window (not shared across tabs)
//   - Is cleared automatically when the tab or browser is closed
//   - Reduces the token exposure window compared to localStorage
//
// IMPORTANT: sessionStorage is still accessible to JavaScript on the same
// origin, so XSS can still read it. This is a defense-in-depth improvement,
// not a complete fix. See the "IDEAL FUTURE STATE" note below.
//
// --------------------------------------------------------------------------
// IDEAL FUTURE STATE (requires backend changes):
//
// The frontend should NOT manage bearer tokens at all. Instead, the backend
// auth endpoints should:
//
//   1. Set the session token in an httpOnly cookie with the following flags:
//        - httpOnly: true    (prevents JavaScript access entirely)
//        - Secure: true      (only sent over HTTPS)
//        - SameSite: Strict  (prevents CSRF attacks)
//        - Path: /           (or scoped to /v3/ and /agent/ as needed)
//        - Max-Age / Expires (match current Redis TTL of 30 days, or shorter)
//
//   2. The following backend endpoints need to be updated to SET the cookie
//      in the response instead of returning { bearerToken: "..." } in JSON:
//        - POST /v3/apiKey/email_login
//        - POST /v3/apiKey/wallet_login
//        - POST /v3/apiKey/wallet_register
//        - POST /v3/apiKey/email_register
//        - POST /v3/apiKey/reset_password (currently returns { newToken })
//        - POST /v3/apiKey/update_password (currently returns { newToken })
//
//   3. The following backend endpoints need to READ the token from the cookie
//      instead of the Authorization header (or support both during migration):
//        - accountAuthMiddleware in restAuthHelpers.ts
//        - All /v3/apiKey/* endpoints that use accountAuthMiddleware
//        - All /agent/* endpoints
//
//   4. The logout endpoint should CLEAR the cookie:
//        - POST /v3/apiKey/logout should set the cookie with Max-Age=0
//
//   5. Add CSRF protection (e.g., double-submit cookie pattern or a
//      synchronizer token) since cookie-based auth is susceptible to CSRF.
//
//   Once the backend sets httpOnly cookies, this entire tokenManager module
//   can be removed and the frontend simply makes fetch/axios calls without
//   manually attaching Authorization headers -- the browser handles it.
//
// --------------------------------------------------------------------------
// BACKEND NOTE (core_backend):
//
// The current backend (core_backend/projects/restapi/src/v3/apiKey/) uses
// Redis-based opaque session tokens, NOT JWTs. The token is a random UUID
// stored in Redis with a 30-day TTL (see setSessionToken in restAuthHelpers.ts).
// The accountAuthMiddleware looks up the token in Redis to resolve the user ID.
//
// Because the tokens are opaque (not JWTs), client-side expiry checking is
// limited to a best-effort approach. The getToken() function below attempts
// JWT exp claim decoding as a safety net, but for opaque tokens it will simply
// return the token as-is. The server-side Redis TTL is the authoritative
// expiry mechanism.
// --------------------------------------------------------------------------

const TOKEN_KEY = "bearerToken";

/**
 * Attempt to decode a JWT payload and check the `exp` claim.
 * Returns true if the token is a valid JWT with an expired `exp` claim.
 * Returns false for non-JWT tokens or JWTs without exp (treat as not expired).
 */
function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      // Not a JWT -- cannot determine expiry client-side.
      return false;
    }

    // Decode the payload (middle segment) from base64url
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const parsed = JSON.parse(decoded);

    if (typeof parsed.exp === "number") {
      // exp is in seconds since epoch
      const nowInSeconds = Math.floor(Date.now() / 1000);
      return parsed.exp < nowInSeconds;
    }

    // No exp claim -- treat as not expired
    return false;
  } catch {
    // Malformed token or decoding error -- treat as not expired.
    // The server will reject invalid tokens anyway.
    return false;
  }
}

/**
 * Retrieve the bearer token from sessionStorage.
 * Returns null if no token exists or if the token is expired (JWT only).
 */
export function getToken(): string | null {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (!token) {
    return null;
  }

  if (isTokenExpired(token)) {
    // Token is expired -- clear it and return null.
    clearToken();
    return null;
  }

  return token;
}

/**
 * Store the bearer token in sessionStorage.
 */
export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

/**
 * Remove the bearer token from sessionStorage.
 */
export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

/**
 * One-time migration helper: if the token exists in localStorage (from the
 * previous implementation) but not in sessionStorage, move it over and
 * remove it from localStorage. This ensures users are not logged out after
 * the code is deployed.
 *
 * Call this once at app initialization (e.g., in the root component or
 * GlobalStateProvider).
 */
export function migrateTokenFromLocalStorage(): void {
  const oldToken = localStorage.getItem(TOKEN_KEY);
  if (oldToken && !sessionStorage.getItem(TOKEN_KEY)) {
    sessionStorage.setItem(TOKEN_KEY, oldToken);
  }
  // Always clean up localStorage regardless
  if (oldToken) {
    localStorage.removeItem(TOKEN_KEY);
  }
}
